import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "@/components/base/Modal";
import { supabase } from "@/lib/supabase";
import { money, variantLabel } from "@/lib/format";
import * as productApi from "@/lib/api/products";
import {
  bulkAdjustSelected,
  bulkClearOverrides,
  bulkSetOverrideAmount,
  buildVariantPriceSaveLines,
  previewEffectivePrice,
  seedOverrideInputs,
  uniformOverridePromotionCandidate,
  type VariantPriceMatrixRow,
} from "@/lib/variant-price-matrix";

export function QuickVariantPriceModal(props: {
  open: boolean;
  onClose: () => void;
  orgId: string;
  productId: string;
  productName: string;
  onSaved: () => void;
}) {
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkAmount, setBulkAmount] = useState("");
  const [adjustAmount, setAdjustAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [forceExceptions, setForceExceptions] = useState(false);

  const { data, error: loadError, isFetching } = useQuery({
    queryKey: ["quick-variant-prices", props.orgId, props.productId, props.open],
    enabled: props.open && !!props.orgId && !!props.productId,
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async (): Promise<{ productSellingPrice: number | null; rows: VariantPriceMatrixRow[] }> => {
      const [productRes, variantsRes] = await Promise.all([
        supabase
          .from("products")
          .select("selling_price")
          .eq("org_id", props.orgId)
          .eq("id", props.productId)
          .maybeSingle(),
        supabase
          .from("variants")
          .select("id, sku, option_values, selling_price")
          .eq("org_id", props.orgId)
          .eq("product_id", props.productId)
          .order("sku"),
      ]);
      if (productRes.error) throw new Error(productRes.error.message);
      if (variantsRes.error) throw new Error(variantsRes.error.message);
      const rows = ((variantsRes.data ?? []) as {
        id: string;
        sku: string | null;
        option_values: Record<string, string> | null;
        selling_price: number | null;
      }[]).map((v) => ({
        id: v.id,
        sku: v.sku,
        label: variantLabel(v.option_values) || "—",
        storedSellingPrice: v.selling_price,
      }));
      const raw = productRes.data?.selling_price;
      return {
        productSellingPrice: raw == null ? null : Number(raw),
        rows,
      };
    },
  });

  useEffect(() => {
    if (!props.open || !data) return;
    setInputs(seedOverrideInputs(data.rows));
    setSelected({});
    setBulkAmount("");
    setAdjustAmount("");
    setError("");
    setForceExceptions(false);
  }, [props.open, data]);

  const rows = data?.rows ?? [];
  const productSellingPrice = data?.productSellingPrice ?? null;
  const selectedIds = rows.filter((r) => selected[r.id]).map((r) => r.id);
  const allSelected = rows.length > 0 && selectedIds.length === rows.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelected({});
      return;
    }
    const next: Record<string, boolean> = {};
    for (const r of rows) next[r.id] = true;
    setSelected(next);
  };

  const targetIds = () => (selectedIds.length ? selectedIds : rows.map((r) => r.id));

  const applyClear = () => {
    setInputs((cur) => bulkClearOverrides(cur, targetIds()));
  };

  const applyBulkSet = () => {
    setInputs((cur) => bulkSetOverrideAmount(cur, targetIds(), bulkAmount));
  };

  const applyAdjust = (sign: 1 | -1) => {
    const n = Number(adjustAmount);
    const result = bulkAdjustSelected(rows, inputs, targetIds(), sign * n, productSellingPrice);
    if (result.ok === false) {
      setError(result.error);
      return;
    }
    setError("");
    setInputs(result.inputs);
  };

  const save = async (asExceptions: boolean) => {
    setBusy(true);
    setError("");
    const built = buildVariantPriceSaveLines(rows, inputs, productSellingPrice);
    if (built.ok === false) {
      setBusy(false);
      setError(built.error);
      return;
    }
    const promote = uniformOverridePromotionCandidate(built.lines, productSellingPrice);
    try {
      if (promote && !asExceptions) {
        await productApi.updateProductSellingPrice(props.orgId, props.productId, promote.amount);
      } else {
        await productApi.saveVariantSellingPrices(props.orgId, props.productId, built.lines);
      }
      props.onSaved();
      props.onClose();
    } catch (e) {
      setError((e as Error).message);
    }
    setBusy(false);
  };

  const builtPreview = buildVariantPriceSaveLines(rows, inputs, productSellingPrice);
  const promote = builtPreview.ok ? uniformOverridePromotionCandidate(builtPreview.lines, productSellingPrice) : null;

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title={`Prices · ${props.productName}`}
      size="drawer"
      footer={
        promote && !forceExceptions ? (
          <div className="flex flex-wrap gap-2 justify-end">
            <button type="button" onClick={() => setForceExceptions(true)} disabled={busy} className="px-4 py-2 border border-slate-200 text-slate-700 text-sm rounded-lg">
              Save as variant exceptions
            </button>
            <button type="button" onClick={() => void save(false)} disabled={busy || isFetching || rows.length === 0} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg disabled:opacity-60">
              {busy ? "Saving..." : "Update product base"}
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => void save(forceExceptions)} disabled={busy || isFetching || rows.length === 0} className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg disabled:opacity-60">
            {busy ? "Saving..." : "Save"}
          </button>
        )
      }
    >
      <div className="space-y-3">
        {(error || loadError) && (
          <div className="px-3 py-2 bg-red-50 text-sm text-red-700 rounded-lg">{error || (loadError as Error).message}</div>
        )}
        <p className="text-xs text-slate-500">
          Product base price {productSellingPrice != null ? money(productSellingPrice) : "is not set"}.
          Leave Override blank to inherit. Enter a value only for an intentional override. Changing the normal product price is Edit product base, not this matrix. Saving prices does not change stock and does not publish content or inventory.
        </p>
        {promote && !forceExceptions && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Every variant is set to the same amount ({money(promote.amount)}). That looks like a product-base change, not four identical exceptions. Update the product base unless these really are intentional overrides.
          </p>
        )}
        <div className="flex flex-wrap items-end gap-2">
          <button type="button" onClick={applyClear} className="px-2 py-1 text-xs rounded-lg border border-slate-200 text-slate-600">
            Use product price
          </button>
          <div className="flex items-center gap-1">
            <input
              value={bulkAmount}
              onChange={(e) => setBulkAmount(e.target.value)}
              className="w-24 px-2 py-1 border border-slate-200 rounded text-xs"
              placeholder="Amount"
              aria-label="Bulk override amount"
            />
            <button type="button" onClick={applyBulkSet} className="px-2 py-1 text-xs rounded-lg border border-slate-200 text-slate-600">
              Set selected
            </button>
          </div>
          <div className="flex items-center gap-1">
            <input
              value={adjustAmount}
              onChange={(e) => setAdjustAmount(e.target.value)}
              className="w-20 px-2 py-1 border border-slate-200 rounded text-xs"
              placeholder="Adj"
              aria-label="Price adjustment amount"
            />
            <button type="button" onClick={() => applyAdjust(1)} className="px-2 py-1 text-xs rounded-lg border border-slate-200 text-slate-600">+ adj</button>
            <button type="button" onClick={() => applyAdjust(-1)} className="px-2 py-1 text-xs rounded-lg border border-slate-200 text-slate-600">− adj</button>
          </div>
        </div>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[min(60vh,520px)]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 sticky top-0">
                <tr>
                  <th className="px-2 py-2 text-left w-8">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Select all variants" />
                  </th>
                  <th className="px-2 py-2 text-left">Variant</th>
                  <th className="px-2 py-2 text-left">SKU</th>
                  <th className="px-2 py-2 text-left">Effective price</th>
                  <th className="px-2 py-2 text-left">Override</th>
                </tr>
              </thead>
              <tbody>
                {isFetching && rows.length === 0 ? (
                  <tr><td colSpan={5} className="px-2 py-8 text-center text-slate-400">Loading variants…</td></tr>
                ) : (
                  rows.map((v) => {
                    const preview = previewEffectivePrice(inputs[v.id] ?? "", productSellingPrice);
                    return (
                      <tr key={v.id} className="border-t border-slate-50">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={!!selected[v.id]}
                            onChange={() => setSelected((s) => ({ ...s, [v.id]: !s[v.id] }))}
                            aria-label={`Select ${v.sku ?? v.id}`}
                          />
                        </td>
                        <td className="px-2 py-2">{v.label}</td>
                        <td className="px-2 py-2 font-mono text-xs">{v.sku ?? "—"}</td>
                        <td className="px-2 py-2">
                          {preview.amount == null ? "—" : money(preview.amount)}
                          <span className="ml-1 text-[10px] uppercase tracking-wide text-slate-400">
                            {preview.kind === "override" ? "override" : "inherited"}
                          </span>
                        </td>
                        <td className="px-2 py-2">
                          <input
                            value={inputs[v.id] ?? ""}
                            onChange={(e) => setInputs((r) => ({ ...r, [v.id]: e.target.value }))}
                            className="w-28 px-2 py-1 border border-slate-200 rounded text-xs"
                            placeholder="Inherited"
                            aria-label={`Override ${v.sku ?? v.id}`}
                          />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Modal>
  );
}
